import {
  type CollectionReference,
  type DocumentData,
  type DocumentSnapshot,
  type QueryDocumentSnapshot,
  collection,
  doc,
  limit,
  orderBy,
  query,
  where,
} from 'firebase/firestore'
import { FirebaseError } from 'firebase/app'

import { callable } from '@/lib/firebase/callable'
import { getFirebaseFirestore } from '@/lib/firebase/app'
import { downloadStorageBytes } from '@/lib/firebase/storage'
import { type Subscription, subscribeCollection, subscribeDoc } from '@/lib/firebase/subscribe'
import {
  BRAND_STYLE_FONTS,
  type BrandStyle,
  type BrandStyleFont,
  type VisualStyleChoice,
  DESIGN_EDIT_RUN_STAGES,
  DESIGN_EDIT_RUN_STATUSES,
  DESIGN_RUN_ERROR_CODES,
  DESIGN_RUN_STAGES,
  DESIGN_RUN_STATUSES,
  type Design,
  type DesignEditRun,
  type DesignEditRunStage,
  type DesignEditRunStatus,
  type DesignRun,
  type DesignRunErrorCode,
  type DesignRunStage,
  type DesignRunStatus,
} from '@/types/design'

export interface DesignScope {
  workspaceId: string
  brandId: string
}

const RECENT_DESIGNS_LIMIT = 20

function brandCollection(scope: DesignScope, name: string): CollectionReference<DocumentData> {
  return collection(
    getFirebaseFirestore(),
    'workspaces',
    scope.workspaceId,
    'brands',
    scope.brandId,
    name,
  )
}

function designsCollection(scope: DesignScope): CollectionReference<DocumentData> {
  return brandCollection(scope, 'designs')
}

function designRunsCollection(scope: DesignScope): CollectionReference<DocumentData> {
  return brandCollection(scope, 'designRuns')
}

function toIsoDate(value: unknown): Date {
  return typeof value === 'string' && value !== '' ? new Date(value) : new Date(0)
}

function toIsoDateOrNull(value: unknown): Date | null {
  return typeof value === 'string' && value !== '' ? new Date(value) : null
}

function toDesign(
  snapshot: DocumentSnapshot<DocumentData> | QueryDocumentSnapshot<DocumentData>,
): Design | undefined {
  const data = snapshot.data()
  if (!data) return undefined
  return {
    id: snapshot.id,
    name: typeof data.name === 'string' ? data.name : '',
    folder: typeof data.folder === 'string' ? data.folder : '',
    currentSnapshotId: typeof data.currentSnapshotId === 'string' ? data.currentSnapshotId : null,
    renderVersion: toNumberOrNull(data.renderVersion),
    revision: typeof data.revision === 'string' ? data.revision : null,
    createdAt: toIsoDate(data.createdAt),
    updatedAt: toIsoDate(data.updatedAt),
  }
}

function toNumberOrNull(value: unknown): number | null {
  return typeof value === 'number' ? value : null
}

function toDesignRunStatus(value: unknown): DesignRunStatus {
  return typeof value === 'string' && (DESIGN_RUN_STATUSES as readonly string[]).includes(value)
    ? (value as DesignRunStatus)
    : 'running'
}

function toDesignRunStage(value: unknown): DesignRunStage {
  return typeof value === 'string' && (DESIGN_RUN_STAGES as readonly string[]).includes(value)
    ? (value as DesignRunStage)
    : 'plan'
}

function toDesignRunErrorCode(value: unknown): DesignRunErrorCode | null {
  return typeof value === 'string' && (DESIGN_RUN_ERROR_CODES as readonly string[]).includes(value)
    ? (value as DesignRunErrorCode)
    : null
}

function toDesignRun(snapshot: QueryDocumentSnapshot<DocumentData>): DesignRun | undefined {
  const data = snapshot.data()
  if (!data) return undefined
  return {
    id: snapshot.id,
    designId: typeof data.designId === 'string' ? data.designId : '',
    postId: typeof data.postId === 'string' ? data.postId : '',
    status: toDesignRunStatus(data.status),
    stage: toDesignRunStage(data.stage),
    errorCode: toDesignRunErrorCode(data.errorCode),
    requestedBy: typeof data.requestedBy === 'string' ? data.requestedBy : '',
    renderVersion: toNumberOrNull(data.renderVersion),
    pageCount: toNumberOrNull(data.pageCount),
    createdAt: toIsoDate(data.createdAt),
    updatedAt: toIsoDate(data.updatedAt),
    completedAt: toIsoDateOrNull(data.completedAt),
  }
}

function designEditRunsCollection(scope: DesignScope): CollectionReference<DocumentData> {
  return brandCollection(scope, 'designEditRuns')
}

function toDesignEditRunStatus(value: unknown): DesignEditRunStatus {
  return typeof value === 'string' &&
    (DESIGN_EDIT_RUN_STATUSES as readonly string[]).includes(value)
    ? (value as DesignEditRunStatus)
    : 'running'
}

function toDesignEditRunStage(value: unknown): DesignEditRunStage {
  return typeof value === 'string' && (DESIGN_EDIT_RUN_STAGES as readonly string[]).includes(value)
    ? (value as DesignEditRunStage)
    : 'read'
}

function toStringOrNull(value: unknown): string | null {
  return typeof value === 'string' ? value : null
}

function toDesignEditRun(snapshot: QueryDocumentSnapshot<DocumentData>): DesignEditRun | undefined {
  const data = snapshot.data()
  if (!data) return undefined
  return {
    id: snapshot.id,
    designId: typeof data.designId === 'string' ? data.designId : '',
    page: typeof data.page === 'number' ? data.page : 1,
    eid: toStringOrNull(data.eid),
    instruction: typeof data.instruction === 'string' ? data.instruction : '',
    status: toDesignEditRunStatus(data.status),
    stage: toDesignEditRunStage(data.stage),
    errorCode: toStringOrNull(data.errorCode),
    revisionBefore: typeof data.revisionBefore === 'string' ? data.revisionBefore : '',
    revisionAfter: toStringOrNull(data.revisionAfter),
    summary: toStringOrNull(data.summary),
    changedEid: toStringOrNull(data.changedEid),
    undone: data.undone === true,
    createdAt: toIsoDate(data.createdAt),
    completedAt: toIsoDateOrNull(data.completedAt),
  }
}

export interface SubscribeDesignInput extends DesignScope {
  designId: string
}

export function subscribeDesign(input: SubscribeDesignInput): Subscription<Design> {
  return subscribeDoc(doc(designsCollection(input), input.designId), toDesign)
}

export interface SubscribeDesignRunsInput extends DesignScope {
  designId: string
}

export function subscribeDesignRuns(input: SubscribeDesignRunsInput): Subscription<DesignRun[]> {
  return subscribeCollection(
    query(
      designRunsCollection(input),
      where('designId', '==', input.designId),
      orderBy('createdAt', 'desc'),
    ),
    (snapshot) =>
      snapshot.docs.map(toDesignRun).filter((run): run is DesignRun => run !== undefined),
  )
}

/** Raldo's edits of one design, newest first. */
export function subscribeDesignEditRuns(
  input: SubscribeDesignRunsInput,
): Subscription<DesignEditRun[]> {
  return subscribeCollection(
    query(designEditRunsCollection(input), where('designId', '==', input.designId)),
    (snapshot) =>
      snapshot.docs
        .map(toDesignEditRun)
        .filter((run): run is DesignEditRun => run !== undefined)
        .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime()),
  )
}

/** The newest design run of one post, or `null` when its media was never designed. */
export function subscribeLatestPostDesignRun(
  scope: DesignScope,
  postId: string,
): Subscription<DesignRun | null> {
  return subscribeCollection(
    query(
      designRunsCollection(scope),
      where('postId', '==', postId),
      orderBy('createdAt', 'desc'),
      limit(1),
    ),
    (snapshot) => {
      const [latest] = snapshot.docs
      return latest ? (toDesignRun(latest) ?? null) : null
    },
  )
}

export function subscribeRecentDesigns(scope: DesignScope): Subscription<Design[]> {
  return subscribeCollection(
    query(designsCollection(scope), orderBy('createdAt', 'desc'), limit(RECENT_DESIGNS_LIMIT)),
    (snapshot) =>
      snapshot.docs.map(toDesign).filter((design): design is Design => design !== undefined),
  )
}

export interface DesignPostMediaInput {
  postId: string
}

export interface DesignPostMediaResult {
  designId: string
  runId: string
}

/** Starts filling a design for a post's media from its slides and attaches it on completion. */
export const designPostMedia = callable<DesignPostMediaInput, DesignPostMediaResult>(
  'designPostMedia',
)

export interface SaveDesignInput {
  designId: string
  html: string
  expectedRevision: string
}

export interface SaveDesignResult {
  revision: string
}

export interface EditDesignInput {
  designId: string
  page: number
  eid: string | null
  instruction: string
}

/** Starts Raldo's edit of one page of a design; resolves with the run to watch. */
export const editDesign = callable<EditDesignInput, { runId: string }>('editDesign')

/** Stops a running edit; nothing is written. */
export const cancelDesignEdit = callable<{ runId: string }, { status: string }>('cancelDesignEdit')

/** Restores the design to before a finished edit; resolves with the restored revision. */
export const undoDesignEdit = callable<{ runId: string }, { revision: string }>('undoDesignEdit')

/** Writes the edited `design.html` of a design when its revision is still the one the editor read. */
export const saveDesign = callable<SaveDesignInput, SaveDesignResult>('saveDesign')

export function designRenderStoragePath(
  workspaceId: string,
  brandId: string,
  designId: string,
  renderVersion: number,
  page: number,
): string {
  return `workspaces/${workspaceId}/brands/${brandId}/workspace/renders/${designId}/v${renderVersion}/page-${page}.png`
}

/** Saves the brand style and confirms the Visual style step, the last step of setup; `onboardingCompleted` says whether setup finished. */
export const confirmVisualStyle = callable<
  { choice: VisualStyleChoice },
  { onboardingCompleted: boolean }
>('confirmVisualStyle')

/** The storage path of the brand's saved style. */
export function brandStyleStoragePath(scope: DesignScope): string {
  return `workspaces/${scope.workspaceId}/brands/${scope.brandId}/workspace/style/style.json`
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isBrandStyleFont(value: unknown): value is BrandStyleFont {
  return (BRAND_STYLE_FONTS as readonly unknown[]).includes(value)
}

/** Reads a parsed `style.json`; `null` when it is not a brand style. */
export function toBrandStyle(value: unknown): BrandStyle | null {
  if (!isPlainObject(value) || typeof value.templateId !== 'string') return null
  if (!isBrandStyleFont(value.font)) return null
  const own = isPlainObject(value.ownDesigns) ? value.ownDesigns.libraryItemIds : undefined
  const ids = Array.isArray(own) ? own.filter((id): id is string => typeof id === 'string') : []
  return {
    templateId: value.templateId,
    accent: typeof value.accent === 'string' ? value.accent : null,
    font: value.font,
    ownDesigns: ids.length > 0 ? { libraryItemIds: ids } : null,
    ...(typeof value.madeFrom === 'string' ? { madeFrom: value.madeFrom } : {}),
  }
}

/** Reads the brand's saved style; `null` while the brand has none. */
export async function readBrandStyle(scope: DesignScope): Promise<BrandStyle | null> {
  try {
    const bytes = await downloadStorageBytes(brandStyleStoragePath(scope))
    return toBrandStyle(JSON.parse(new TextDecoder().decode(bytes)))
  } catch (cause) {
    if (cause instanceof FirebaseError && cause.code === 'storage/object-not-found') return null
    throw cause
  }
}
